import { useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { AppDialog } from "../../components/AppDialog";
import { AppDialogHeader } from "../../components/AppDialogHeader";
import { addAlbumTagApi, removeAlbumTagApi } from "../../../albumsApi";
import type { Album } from "../../../types";

const MAX_TAG_LENGTH = 40;

// Lightweight tag editor reached from AlbumOptionsDialog.tsx's "Manage
// Tags" action - not a taxonomy admin screen, just existing tags as
// removable chips plus a single text field to add a free-text one. Same
// AppDialog/AppDialogHeader shell every other dialog in this app uses.
export function AlbumTagsDialog({ album, onClose }: { album: Album | null; onClose: () => void }) {
  const [newTag, setNewTag] = useState("");
  const [error, setError] = useState("");
  const [busyTag, setBusyTag] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  if (!album) return null;
  const tags = album.tags || [];

  function handleClose(): void {
    setNewTag("");
    setError("");
    onClose();
  }

  async function handleAdd(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError("");
    const trimmed = newTag.trim();
    if (!trimmed) return;
    if (trimmed.length > MAX_TAG_LENGTH) {
      setError(`Tag is too long (max ${MAX_TAG_LENGTH} characters).`);
      return;
    }
    setAdding(true);
    const result = await addAlbumTagApi(album!.id, trimmed);
    setAdding(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setNewTag("");
  }

  async function handleRemove(tag: string): Promise<void> {
    setError("");
    setBusyTag(tag);
    const result = await removeAlbumTagApi(album!.id, tag);
    setBusyTag(null);
    if (!result.ok) setError(result.error);
  }

  return (
    <AppDialog open onClose={handleClose} maxWidth="xs" fullWidth>
      <AppDialogHeader icon="bi-tags" title="Manage Tags" onBack={handleClose} />
      <DialogContent sx={{ p: 4, pt: 2 }}>
        <Box sx={{ display: "grid", gap: 2 }}>
          {tags.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {tags.map((tag) => (
                <span className="albums-tag-chip albums-tag-chip-removable" key={tag}>
                  {tag}
                  <button
                    type="button"
                    className="albums-tag-chip-remove"
                    aria-label={`Remove tag ${tag}`}
                    disabled={busyTag === tag}
                    onClick={() => handleRemove(tag)}
                  >
                    <i className="bi bi-x" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </Box>
          ) : (
            <p className="small muted mb-0">No tags yet.</p>
          )}

          <Box component="form" onSubmit={handleAdd} sx={{ display: "flex", gap: 1 }}>
            <TextField
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="e.g., Trips, Romantic..."
              size="small"
              fullWidth
              slotProps={{ htmlInput: { maxLength: MAX_TAG_LENGTH } }}
            />
            <Button type="submit" variant="contained" color="primary" disabled={adding || !newTag.trim()}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {error}
            </Alert>
          )}

          <Button variant="outlined" color="primary" onClick={handleClose}>
            Done
          </Button>
        </Box>
      </DialogContent>
    </AppDialog>
  );
}
